import type { EventHint } from "@sentry/react";
import { captureMessage } from "./init";
import { getRoutePattern, type RouteMatcher } from "./routePattern";

export type { RouteMatcher };

export const APP_BROKEN_TAG = "app_broken";
export const APP_BROKEN_DETAIL_TAG = "app_broken_detail";
/** Normalized route (`:param` placeholders, never a query string) the surface rendered on.
 * Raw URLs are unusable here: the hash-routed apps collapse Sentry's own `url` tag to one
 * aggregate value, and raw ids would blow up tag cardinality. See `normalizeAppBrokenRoute`. */
export const APP_BROKEN_ROUTE_TAG = "app_broken_route";

/**
 * Which terminal user-facing surface rendered:
 * - `error_boundary`: a full-page ErrorPage from a React error boundary or route error
 * - `error_banner`: the error banner (full-page or strip variant)
 * - `network_fallback`: an offline / connectivity fallback — usually the user's network, not our bug
 */
export type AppBrokenSource = "error_boundary" | "error_banner" | "network_fallback";

// Sentry's capture hint is an exclusive union: CaptureContext fields (tags, extra)
// and EventHint fields (mechanism) must not be mixed at the top level. Combining
// them requires nesting the former under `captureContext` — these helpers are the
// sanctioned way to do that.

// `mechanism.handled: false` is what flips the Sentry session to "crashed", which is the
// numerator of the Release Health crash rate. The question each surface answers is not
// "did the JS heap survive" but "did this person hit a broken product". See WEBP-516.
//
//   error_boundary   CRASH — a boundary replaced the UI with an error page; they cannot
//                    continue what they were doing.
//   error_banner     CRASH — we told them something went wrong. That they *can* reload
//                    past it does not make it not a break, and the dominant driver
//                    (moduleLoadError from a stale chunk hash after a deploy) is a real
//                    bug that belongs in the number rather than reclassified out of it.
//   network_fallback not a crash — this is the person's own connectivity, not our
//                    failure. Counting it would make the metric unactionable.
//
// A fallthrough capture with no user-facing surface at all (`unhandledHint`) is likewise
// not a crash: nothing was shown, so nothing broke for them. `appIsDead` in
// `captureReactException` still forces a crash regardless of source.
const CRASHING_SOURCES: ReadonlySet<AppBrokenSource> = new Set<AppBrokenSource>(["error_boundary", "error_banner"]);

/** Sentry's default grouping for these `app_broken_detail` values hashes the error message, and for
 * a module load failure that message carries the hashed chunk filename — so every deploy that
 * rebuilds a chunk opens a brand new issue for the same breakage. A constant fingerprint collapses
 * them, and each app reports into its own Sentry project, so that is one issue per app.
 *
 * This table lives beside the hint builders rather than at the call sites because the decision about
 * which error types need stable grouping is a telemetry concern. Both `appBrokenHint` and
 * `unhandledAppBrokenHint` apply it automatically, so a surface cannot silently omit the override or
 * duplicate the table; anything added here has to be applied by every builder that reports the same
 * breakages.
 *
 * NOTE: infra/pulumi/sentry-fingerprint-rules.ts carries server-side rules for four module-load
 * message wordings, and a server rule wins over the SDK value. This covers the wordings it does not
 * name (ChunkLoadError, "Loading chunk N failed", Firefox's phrasing).
 *
 * Do NOT retire those server rules in favour of this table without first closing the coverage gap
 * between them: a server rule applies to EVERY capture path, while this applies only where a hint
 * builder is used, and `appBrokenHint`'s one production call site is ErrorPage.tsx. A module-load
 * failure captured anywhere else — a global unhandledrejection, a dynamic import failing outside a
 * React boundary — would fall back to grouping by a message containing the hashed chunk name, which
 * mints a fresh issue per deploy. That is the churn this table exists to stop.
 * TODO: route the remaining capture paths through a hint, then consolidate on one owner. */
export const STABLE_APP_BROKEN_FINGERPRINTS: Readonly<Record<string, string[] | undefined>> = {
  "module-load-error": ["module-load-error"],
};

// Shared skeleton for the hint builders below, so the exclusive-union nesting can't
// diverge between them.
function mechanismHint(
  tags: Record<string, string>,
  handled: boolean,
  opts?: { extra?: Record<string, unknown>; fingerprint?: string[] },
): EventHint {
  return {
    mechanism: { type: "generic", handled },
    captureContext: {
      tags,
      ...(opts?.extra ? { extra: opts.extra } : {}),
      ...(opts?.fingerprint ? { fingerprint: opts.fingerprint } : {}),
    },
  };
}

function appBrokenTags(source: AppBrokenSource, detail?: string): Record<string, string> {
  return {
    [APP_BROKEN_TAG]: source,
    ...(detail ? { [APP_BROKEN_DETAIL_TAG]: detail } : {}),
  };
}

/** Hint for the final fallthrough captures in app onError handlers: the legacy
 * `handled: "no"` string tag for saved-search continuity.
 *
 * "Unhandled" here means "no call site handled it before the global handler", not "the
 * app died". There is no user-facing surface on this path, so the mechanism stays
 * `handled: true` and the session is not crashed. Note the tag and the mechanism
 * therefore disagree — the tag is kept verbatim for saved-search continuity. */
export function unhandledHint(opts?: { extra?: Record<string, unknown> }): EventHint {
  return mechanismHint({ handled: "no" }, true, { extra: opts?.extra });
}

/** Hint for a final fallthrough capture that ALSO renders a terminal user-facing
 * surface: `unhandledHint`'s mechanism/tags plus `appBrokenHint`'s tags on a single
 * event, so the exception and the surface report stay one Sentry event instead of an
 * unlinked pair. The surface must skip its own `captureAppBrokenMessage` when the
 * capture used this hint (see `alreadyReported` on the error banner API). */
export function unhandledAppBrokenHint(
  source: AppBrokenSource,
  opts: { detail: string; extra?: Record<string, unknown> },
): EventHint {
  return mechanismHint({ handled: "no", ...appBrokenTags(source, opts.detail) }, !CRASHING_SOURCES.has(source), {
    extra: opts.extra,
    // Same lookup as `appBrokenHint`. This path reports the same breakages through a different
    // surface, so leaving it out would put module-load failures that happen to arrive here back on
    // message-based grouping — a fresh issue per deploy, which is the exact thing the table exists
    // to stop.
    fingerprint: STABLE_APP_BROKEN_FINGERPRINTS[opts.detail],
  });
}

/** Hint for capturing an exception at a terminal user-facing surface.
 *
 * A fingerprint replaces Sentry's default grouping for that event. One is attached automatically for
 * every `detail` in `STABLE_APP_BROKEN_FINGERPRINTS`, because the default (message-based) grouping
 * of those is unstable across deploys — a hashed chunk filename in the message otherwise mints a
 * fresh issue per build. Pass `fingerprint` to override that choice. */
export function appBrokenHint(source: AppBrokenSource, opts?: { detail?: string; fingerprint?: string[] }): EventHint {
  const fingerprint = opts?.fingerprint ?? (opts?.detail ? STABLE_APP_BROKEN_FINGERPRINTS[opts.detail] : undefined);

  return mechanismHint(appBrokenTags(source, opts?.detail), !CRASHING_SOURCES.has(source), { fingerprint });
}

/** Report a terminal user-facing surface that rendered without an exception object
 * to capture (an errorType-only error page, a strip banner).
 *
 * The `capture` default is safe pre-Sentry-init: default params resolve at call time
 * against the live `./init` binding, which queues calls until Sentry loads. It exists
 * as a parameter so tests can inject a recorder without module mocking — and it stays LAST,
 * after `route`, so pass `undefined` for `route` when injecting one. */
export function captureAppBrokenMessage(
  source: AppBrokenSource,
  detail: string,
  route?: string,
  capture = captureMessage,
): void {
  capture(`app_broken: ${source} shown (${detail})`, {
    level: "error",
    tags: {
      [APP_BROKEN_TAG]: source,
      [APP_BROKEN_DETAIL_TAG]: detail,
      ...(route ? { [APP_BROKEN_ROUTE_TAG]: route } : {}),
    },
  });
}

// Shapes that are always an opaque identifier, never a hand-written route segment.
// Deliberately conservative: a segment is only replaced when it cannot be a literal route
// name, so real routes such as /OAuth2Callback survive verbatim. Named `:param` placeholders
// come from the router; this is the fallback for whatever it can't match.
const ID_SEGMENT_PATTERNS = [
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // UUID
  /^\d+$/, // numeric id
  /^[0-9a-f]{12,}$/i, // long hex token, which subsumes the 24-char Mongo ObjectId
  /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{20,}$/i, // long opaque token (invite code, JWT segment, ...)
];

// Applied to every URL part before anything else. /OAuth2Callback carries a single-use
// authorization code in its query string, so no code path may let one reach a tag.
const stripQuery = (part: string): string => part.split("?")[0]!;

const scrubIdSegments = (path: string): string =>
  path
    .split("/")
    .map((segment) => (ID_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment)) ? ":id" : segment))
    .join("/");

/** Low-cardinality route string for the `app_broken_route` tag.
 *
 * The hash is part of the result because home, student and teach all use `createHashRouter`:
 * dropping it collapses every event onto one value, which is the blind spot this tag exists to
 * remove. Pass the app's `matchRoutes` (bound to its route tree, as `_app.tsx` already does for
 * `useRouteSpans`) to get real route patterns like `/schools/story/:storyId`; without it the
 * shape-based scrub above is used. Query strings are stripped unconditionally. */
export function normalizeAppBrokenRoute(url: { pathname: string; hash: string }, matchRoutes?: RouteMatcher): string {
  try {
    // `pathname` should hold neither, but a malformed URL must not leak a query string.
    const pathname = scrubIdSegments(stripQuery(url.pathname.split("#")[0]!));
    const hashPath = stripQuery(url.hash.replace(/^#/, ""));
    if (!hashPath) return pathname;

    const pattern = matchRoutes ? getRoutePattern(hashPath, matchRoutes) : hashPath;
    return `${pathname}#${scrubIdSegments(pattern)}`;
    // Telemetry must never break the error handler reporting through it.
    // eslint-disable-next-line no-catch-all/no-catch-all -- best-effort tagging: any failure here must degrade to a placeholder, never propagate out of the error handler
  } catch {
    return "unknown";
  }
}
